import { useCallback, useMemo, useState, type ReactNode } from 'react'
import type { CheckInsBySet } from '../../../hooks/useCheckIns'
import { Tabs, TabsContent } from '../../../components/ui/tabs'
import { DayTabsList } from '../../../components/DayTabsList'
import { cn } from '../../../lib/utils'
import { DayCutoffContext } from '../../../contexts/DayCutoffContext'
import { useScheduleSettings } from '../../../hooks/useScheduleSettings'
import { useFavoriteIdSet } from '../../../contexts/FavoriteFriendsContext'
import { useScheduleFilter } from '../../../lib/scheduleFilterSession'
import type { ScheduleFilter } from './scheduleFilter'
import type { PartialRangeEntryPoint, ScheduleSurface, SetInterestEntryPoint } from '../../../lib/analytics'
import type { SetDetailTab } from '../../../components/SetDetailDialog'
import type { OneShotTipDismissReason } from '../../../components/OneShotTip'
import type { SetFeed } from '../../../hooks/useSetPosts'
import type { TotemMarking } from '../../../hooks/useTotemSlots'
import type { SetRangesByUser } from '../../../hooks/useGroupInterests'
import type { Set as FestivalSet, Stage, Artist, PartialRange, UserProfile } from '../../../types'
import { DayGrid } from './DayGrid'
import { ScheduleMarkersProvider } from './ScheduleMarkersProvider'
import { ScheduleSearch } from './ScheduleSearch'
import { ScheduleSkeleton } from './ScheduleSkeleton'
import { EmptyScheduleMessage } from './EmptyScheduleMessage'
import { useScheduleUpdatedSets } from '../../../hooks/useScheduleUpdatedSets'
import { WallpaperDialog } from './wallpaper/WallpaperDialog'

/** `dayCutoff` is the event's `eventDayCutoff()` — the grid and now-line anchor post-midnight sets with it. */
export function ScheduleView({ dayCutoff, ...props }: ScheduleViewProps & { dayCutoff: number }) {
  return (
    <DayCutoffContext.Provider value={dayCutoff}>
      <ScheduleViewBody {...props} />
    </DayCutoffContext.Provider>
  )
}

type ScheduleViewProps = Parameters<typeof ScheduleViewBody>[0]

function ScheduleViewBody({
  days,
  defaultDay,
  eventId,
  onSelectSearchResult,
  sets,
  setsLoading,
  stages,
  artists,
  hasSchedule,
  interestedSetIds,
  goingSetIds,
  onCycleSet,
  setUserIds,
  setRanges,
  setCheckIns,
  ownRanges,
  onUpdateOwnRange,
  userProfiles,
  currentUserId,
  tipSetId,
  onDismissTip,
  feed,
  showFeedBadge,
  totem,
  onOpenMember,
  highlightSetId,
  onHighlightConsumed,
  timezone,
  festivalMode,
  shareFab,
  wallpaper,
  surface,
  defaultTab,
}: {
  days: string[]
  defaultDay: string | null
  eventId: string
  onSelectSearchResult: (set: FestivalSet) => void
  /** Every set for the event; the view filters down to the selected day. */
  sets: FestivalSet[]
  setsLoading: boolean
  stages: Stage[]
  artists: Record<string, Artist>
  hasSchedule: boolean
  interestedSetIds: Set<string>
  goingSetIds: Set<string>
  onCycleSet: (setId: string, entryPoint: SetInterestEntryPoint) => void
  setUserIds: { interested: Record<string, string[]>; going: Record<string, string[]> }
  setRanges?: SetRangesByUser
  /** Festival mode only: who's checked in at each set. */
  setCheckIns?: CheckInsBySet
  ownRanges?: ReadonlyMap<string, PartialRange>
  onUpdateOwnRange?: (setId: string, partialRange: PartialRange | null, entryPoint?: PartialRangeEntryPoint) => void
  userProfiles: Record<string, UserProfile>
  currentUserId: string | null
  tipSetId?: string | null
  onDismissTip?: (via: OneShotTipDismissReason) => void
  feed?: SetFeed
  /** Show the feed badge on set cards. Off inside festival mode, where the
   * card should stay focused on interest + totems; the dialog's feed tab stays. */
  showFeedBadge?: boolean
  totem?: TotemMarking
  /** Routes member rows in the set dialog to a page-owned view (festival mode). */
  onOpenMember?: (uid: string) => void
  highlightSetId?: string | null
  onHighlightConsumed?: () => void
  timezone?: string
  festivalMode?: { eventId: string; startDate: string; endDate?: string | null }
  shareFab?: ReactNode
  /** Own schedule only; omit on a read-only shared view. Enables the lock screen image. */
  wallpaper?: { eventName: string }
  /** Which surface is rendering this schedule — tags the FAB-tap events. */
  surface: ScheduleSurface
  defaultTab?: SetDetailTab
}) {
  // The day lives here, not in the URL: it's browsing state inside the view, so
  // leaving the schedule (which unmounts this) drops it and coming back opens on
  // `defaultDay` — the day the festival is actually on — again.
  const [pickedDay, setPickedDay] = useState<string | null>(null)
  const [wallpaperOpen, setWallpaperOpen] = useState(false)
  const selectedDay = (pickedDay && days.includes(pickedDay) ? pickedDay : null) ?? defaultDay

  // A deep-linked / searched set carries its own day (?set= arrives from the Now
  // tab, the search box, a notification) — follow it so the block we're about to
  // flash is on screen. Same "previous value during render" pattern as the slide
  // direction below; the day resolves late, once the sets have loaded.
  const highlightDay = highlightSetId ? sets.find(s => s.id === highlightSetId)?.day : undefined
  const [prevHighlightDay, setPrevHighlightDay] = useState(highlightDay)
  if (highlightDay !== prevHighlightDay) {
    setPrevHighlightDay(highlightDay)
    if (highlightDay) setPickedDay(highlightDay)
  }

  const daySets = useMemo(
    () => (selectedDay ? sets.filter(s => s.day === selectedDay) : []),
    [sets, selectedDay],
  )
  const updatedSetIds = useScheduleUpdatedSets(eventId, selectedDay, currentUserId, daySets, setsLoading)

  const {
    visibleStages,
    allStagesWithVisibility,
    updateStageOrder,
    toggleStageVisibility,
    resetToDefault,
    hasCustomSettings,
  } = useScheduleSettings(currentUserId, eventId, stages)

  // "Favorites" only joins the cycle when a favorited friend has marked a set at
  // this event — otherwise it would land on a schedule that's always empty. A
  // persisted "favorites" reads as "none" until that's true (e.g. while loading).
  const favoriteIdSet = useFavoriteIdSet()
  const hasCrewFavorites = useMemo(
    () =>
      [setUserIds.interested, setUserIds.going].some(bySet =>
        Object.values(bySet).some(uids => uids.some(u => favoriteIdSet.has(u))),
      ),
    [setUserIds, favoriteIdSet],
  )
  const [storedFilter, setFilter] = useScheduleFilter(eventId, surface)
  const filter: ScheduleFilter = storedFilter === 'favorites' && !hasCrewFavorites ? 'none' : storedFilter
  const cycleFilter = useCallback(() => {
    setFilter(
      filter === 'none'
        ? 'interested'
        : filter === 'interested'
          ? 'going'
          : filter === 'going' && hasCrewFavorites
            ? 'favorites'
            : 'none',
    )
  }, [filter, hasCrewFavorites, setFilter])

  // Slide the day grid in from the direction of travel when the day changes
  // (forward = a later day, back = an earlier one), so switching days — and the
  // search jump in particular — reads as a deliberate move, not a content flip.
  // "Previous value during render" pattern: when the day changes we derive the
  // direction and store it, re-rendering once before commit (no refs/effects).
  const [prevDay, setPrevDay] = useState(selectedDay)
  const [slideClass, setSlideClass] = useState('')
  if (selectedDay !== prevDay) {
    setSlideClass(
      !prevDay
        ? ''
        : days.indexOf(selectedDay ?? '') > days.indexOf(prevDay)
          ? 'animate-day-in-right'
          : 'animate-day-in-left',
    )
    setPrevDay(selectedDay)
  }

  // Two emptinesses share the branch below — no schedule at all, or a bare day
  // on an event that has one. Both take the offer: festivals routinely publish
  // a day at a time, so a missing day is the same signal.
  const emptyMessage = <EmptyScheduleMessage eventId={eventId} emptyDay={hasSchedule} />

  if (days.length === 0 || !selectedDay) {
    return (
      <div className="flex-1 min-h-0 flex flex-col gap-3">
        <ScheduleSearch
          sets={sets}
          loading={setsLoading}
          artists={artists}
          stages={stages}
          multiDay={false}
          onSelect={onSelectSearchResult}
        />
        <DayTabsList loading bleed />
        <div className="flex-1 min-h-0 flex flex-col">
          <ScheduleSkeleton />
        </div>
      </div>
    )
  }

  return (
    <ScheduleMarkersProvider
      eventId={eventId}
      surface={surface}
      currentUserId={currentUserId}
      day={selectedDay}
      daySets={daySets}
      timezone={timezone}
    >
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <ScheduleSearch
        sets={sets}
        loading={setsLoading}
        artists={artists}
        stages={stages}
        multiDay={days.length > 1}
        onSelect={onSelectSearchResult}
      />
      <Tabs
        value={selectedDay}
        onValueChange={setPickedDay}
        className="flex-1 min-h-0 flex flex-col gap-3"
      >
        <DayTabsList days={days} bleed />
        <TabsContent value={selectedDay} className="flex-1 min-h-0 flex flex-col">
        {setsLoading ? (
          <ScheduleSkeleton />
        ) : !hasSchedule || daySets.length === 0 ? (
          <ScheduleSkeleton message={emptyMessage} stages={stages} />
        ) : (
          <div key={selectedDay} className={cn('flex-1 min-h-0 flex flex-col', slideClass)}>
            <DayGrid
              day={selectedDay}
              sets={daySets}
              updatedSetIds={updatedSetIds}
              stages={visibleStages}
              artists={artists}
              interestedSetIds={interestedSetIds}
              goingSetIds={goingSetIds}
              onCycleSet={onCycleSet}
              setUserIds={setUserIds}
              setRanges={setRanges}
              setCheckIns={setCheckIns}
              ownRanges={ownRanges}
              onUpdateOwnRange={onUpdateOwnRange}
              userProfiles={userProfiles}
              currentUserId={currentUserId}
              filter={filter}
              onCycleFilter={cycleFilter}
              allStagesWithVisibility={allStagesWithVisibility}
              hasCustomSettings={hasCustomSettings}
              onReorder={updateStageOrder}
              onToggleVisibility={toggleStageVisibility}
              onResetSettings={resetToDefault}
              tipSetId={tipSetId}
              onDismissTip={onDismissTip}
              feed={feed}
              showFeedBadge={showFeedBadge}
              totem={totem}
              onOpenMember={onOpenMember}
              highlightSetId={highlightSetId}
              onHighlightConsumed={onHighlightConsumed}
              timezone={timezone}
              festivalMode={festivalMode}
              shareFab={shareFab}
              onOpenWallpaper={wallpaper ? () => setWallpaperOpen(true) : undefined}
              eventId={eventId}
              surface={surface}
              defaultTab={defaultTab}
            />
          </div>
        )}
        </TabsContent>
      </Tabs>
      {wallpaper && (
        <WallpaperDialog
          open={wallpaperOpen}
          onOpenChange={setWallpaperOpen}
          eventId={eventId}
          eventName={wallpaper.eventName}
          surface={surface}
          days={days}
          initialDay={selectedDay}
          sets={sets}
          stages={allStagesWithVisibility}
          artists={artists}
          interestedSetIds={interestedSetIds}
          goingSetIds={goingSetIds}
          ownRanges={ownRanges}
        />
      )}
    </div>
    </ScheduleMarkersProvider>
  )
}
